import React, { useState, useEffect } from 'react';
import { Squad } from '../../../types';
import { useCurriculumAdapter } from '../../curriculum/hooks/useCurriculumAdapter';

interface LevelCarouselProps {
  squad: Squad;
  onLevelSelect: (levelData: {
    id: string;
    title: string;
    missions: string[];
  }) => void;
}

const LevelCarousel: React.FC<LevelCarouselProps> = ({ squad, onLevelSelect }) => {
  const adapter = useCurriculumAdapter({ squad });
  const levels = adapter.levels;
  // Compute the default "Next" level based on completed levels
  const computeNextLevelId = (): string => {
    const completed = squad.completedLevels || [];
    const nums = completed.map((id) => parseInt(id)).filter((n) => !isNaN(n));
    if (nums.length > 0) {
      const nextNum = Math.min(Math.max(Math.max(...nums) + 1, 1), levels.length);
      return String(nextNum);
    }
    // If nothing completed yet, first level is next
    return '1';
  };

  const defaultSelectedId = computeNextLevelId();
  const defaultIndex = adapter.getLevelIndex(defaultSelectedId);

  const [centerIndex, setCenterIndex] = useState(Math.max(0, defaultIndex));
  const [selectedLevelId, setSelectedLevelId] = useState(defaultSelectedId);
  const currentLevelId = squad.currentLessonId || defaultSelectedId;
  
  // Reset carousel when squad changes
  useEffect(() => {
    const nextId = computeNextLevelId();
    const nextIndex = adapter.getLevelIndex(nextId);
    setCenterIndex(Math.max(0, nextIndex));
    setSelectedLevelId(nextId);
  }, [squad.id, squad.currentLessonId, squad.completedLevels, adapter]);

  // Get 5 levels centered around the center index
  const getVisibleLevels = () => {
    const visibleLevelsArray = [];
    for (let i = -2; i <= 2; i++) {
      const index = centerIndex + i;
      if (index >= 0 && index < levels.length) {
        visibleLevelsArray.push({
          ...levels[index],
          position: i,
          isCenter: i === 0
        });
      } else {
        visibleLevelsArray.push({
          id: `empty-${i}`,
          title: '',
          missions: [],
          position: i,
          isCenter: i === 0,
          isEmpty: true
        });
      }
    }
    return visibleLevelsArray;
  };

  const handleLevelClick = (level: any) => {
    if (level.isEmpty) return;
    
    const levelIndex = adapter.getLevelIndex(level.id);
    setCenterIndex(levelIndex);
    setSelectedLevelId(level.id);
    
    // Pass level data to parent
    onLevelSelect({
      id: level.id,
      title: level.title,
      missions: level.missions?.map((mission: any) => mission.text) || []
    });
  };

  const visibleLevels = getVisibleLevels();

  return (
    <div className="level-carousel">
      <div className="level-carousel-track">
        {visibleLevels.map((level, index) => {
          if (level.isEmpty) {
            return <div key={level.id} className="level-card-slot empty" />;
          }

          const levelIndex = adapter.getLevelIndex(level.id);
          const status = adapter.getLevelStatus(level.id, currentLevelId, squad.completedLevels || []);
          const isSelected = level.id === selectedLevelId;
          // Music-only: show short theme (e.g., Harmony, Rhythm). Others: full title.
          const displayName = adapter.id === 'music'
            ? adapter.getShortLevelName(level.title)
            : level.title;

          return (
            <div
              key={level.id}
              className={`level-card-slot ${level.isCenter ? 'center' : ''} ${status} ${isSelected ? 'selected' : ''}`}
              onClick={() => handleLevelClick(level)}
            >
              <div className="level-card-mini">
                <div className="level-name">{displayName}</div>
                <div className="level-number-mini">{levelIndex + 1}</div>
                <div className="level-status-indicator">
                  {status === 'completed' && <span className="status-icon">✓</span>}
                  {status === 'next' && <span className="status-text">Next</span>}
                  {status === 'upcoming' && <span className="status-icon">?</span>}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default LevelCarousel; 